import { useCallback, useEffect, useRef } from 'react';

/**
 * Mirrors the filters the user picks by hand into the reserved "Latest" view, so the state they
 * last filtered into is always one chip away — after a reload, after a trip through another view,
 * or on another device. See docs/LATEST_VIEW.md.
 *
 * Nothing here decides *what* a filter is: the page arms a write by calling `markChange` from the
 * handlers the user drives, which is what keeps a reload, a page change, the back button or the
 * page applying a view of its own accord from counting as a change the user made.
 */

export type ChangeKind = 'filter' | 'search';

/** A change waits this long before it is written, so a burst of picks becomes one request. */
const DEBOUNCE_MS: Record<ChangeKind, number> = {
  // A dropdown is one decision: write it as soon as React has settled.
  filter: 150,
  // The search box is one decision per keystroke: wait for the typing to stop.
  search: 800,
};

/**
 * Two filter sets that differ only by key order, by the order of `hidden_columns`, or by an empty
 * value spelled `undefined` here and `null` there (the API stores every key) are the same filters.
 */
export function serializeViewFilters(filters: object | null | undefined): string {
  if (!filters) return '';
  const entries = Object.entries(filters as Record<string, unknown>)
    .filter(([, value]) =>
      value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)
    )
    .map(([key, value]) => [key, Array.isArray(value) ? [...value].map(String).sort() : value] as [string, unknown])
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

interface UseLatestViewOptions<F> {
  /** The filters as a view would store them — what the page's `getCurrentFilters()` returns. */
  filters: F;
  /** What the "Latest" view holds now, so a state it already holds is not written again. */
  latestFilters: F | null | undefined;
  save: (filters: F) => Promise<unknown>;
}

export function useLatestView<F extends object>({ filters, latestFilters, save }: UseLatestViewOptions<F>) {
  const filtersKey = serializeViewFilters(filters);
  const latestKey = serializeViewFilters(latestFilters);

  // Armed by the page, consumed by the effect below. A ref, not state: arming must not re-render.
  const armedRef = useRef<ChangeKind | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Writes are chained, so two of them cannot land out of order and leave the older one stored.
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());
  // What the write needs when it fires, a debounce after the render that armed it. Synced from an
  // effect declared first, so the effects below it already see this commit's values.
  const currentRef = useRef({ filters, latestKey, save });
  useEffect(() => {
    currentRef.current = { filters, latestKey, save };
  });

  /** Tell the "Latest" view to follow the change this handler is about to make. */
  const markChange = useCallback((kind: ChangeKind = 'filter') => {
    armedRef.current = kind;
  }, []);

  /** Drop an armed write — for the filter writes the page makes itself (applying a view, Reset). */
  const cancelPendingChange = useCallback(() => {
    armedRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const { filters: snapshot, save: write } = currentRef.current;
    // A background convenience: a failed write only means the chip is one change behind, so it is
    // swallowed rather than toasted over whatever the user is doing.
    queueRef.current = queueRef.current.then(() => write(snapshot)).catch(() => undefined);
  }, []);

  useEffect(() => {
    const kind = armedRef.current;
    if (!kind) return;
    armedRef.current = null;
    // Re-picking a value that was already on screen, or landing back on what "Latest" already
    // holds, is nothing to write.
    if (filtersKey === currentRef.current.latestKey) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, DEBOUNCE_MS[kind]);
  }, [filtersKey, flush]);

  // Leaving the page with a change still waiting out its debounce writes it on the way out —
  // typing a search and clicking away is still a filter the user picked.
  useEffect(() => () => {
    if (timerRef.current) flush();
  }, [flush]);

  return { markChange, cancelPendingChange };
}
